import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap, ZoomControl, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { PlotPopup } from './PlotPopup';
import { LoadingState } from '../UI/LoadingState';
import { ErrorState } from '../UI/ErrorState';
import { NoResultsState } from '../UI/NoResultsState';
import { MapControls } from './MapControls';
interface MapViewProps {
  selectedFilters: {
    division: string;
    district: string;
    upazila: string;
    mouja: string;
    sheetNo: string;
  };
  addToCart: (khatian: any) => void;
}
// Location data mapping for coordinates and zoom levels
const locationData = {
  divisions: {
    dhaka: {
      coordinates: [23.8103, 90.4125],
      zoomLevel: 8
    },
    chittagong: {
      coordinates: [22.3569, 91.7832],
      zoomLevel: 8
    },
    rajshahi: {
      coordinates: [24.3745, 88.6042],
      zoomLevel: 8
    },
    khulna: {
      coordinates: [22.8456, 89.5403],
      zoomLevel: 8
    },
    barisal: {
      coordinates: [22.701, 90.3535],
      zoomLevel: 8
    },
    sylhet: {
      coordinates: [24.8949, 91.8687],
      zoomLevel: 8
    },
    rangpur: {
      coordinates: [25.7439, 89.2752],
      zoomLevel: 8
    },
    mymensingh: {
      coordinates: [24.7471, 90.4203],
      zoomLevel: 8
    }
  },
  districts: {
    dhaka_district: {
      coordinates: [23.7104, 90.4074],
      zoomLevel: 10
    },
    gazipur: {
      coordinates: [24.0958, 90.4125],
      zoomLevel: 10
    },
    narayanganj: {
      coordinates: [23.6238, 90.5],
      zoomLevel: 11
    },
    tangail: {
      coordinates: [24.2498, 89.9165],
      zoomLevel: 10
    },
    narsingdi: {
      coordinates: [23.9322, 90.7157],
      zoomLevel: 11
    },
    munshiganj: {
      coordinates: [23.5422, 90.5305],
      zoomLevel: 11
    }
  },
  upazilas: {
    dhaka_north: {
      coordinates: [23.8103, 90.4125],
      zoomLevel: 12
    },
    dhaka_south: {
      coordinates: [23.7104, 90.4074],
      zoomLevel: 12
    },
    savar: {
      coordinates: [23.8583, 90.25],
      zoomLevel: 12
    },
    keraniganj: {
      coordinates: [23.7834, 90.3874],
      zoomLevel: 12
    },
    dhamrai: {
      coordinates: [23.9167, 90.2167],
      zoomLevel: 12
    },
    gazipur_sadar: {
      coordinates: [24.0958, 90.4125],
      zoomLevel: 12
    },
    kaliakair: {
      coordinates: [24.1083, 90.275],
      zoomLevel: 12
    },
    kapasia: {
      coordinates: [24.1833, 90.5667],
      zoomLevel: 12
    },
    narayanganj_sadar: {
      coordinates: [23.6238, 90.5],
      zoomLevel: 12
    },
    rupganj: {
      coordinates: [23.75, 90.57],
      zoomLevel: 12
    },
    sonargaon: {
      coordinates: [23.65, 90.6],
      zoomLevel: 12
    }
  },
  moujas: {
    uttara: {
      coordinates: [23.8758, 90.3795],
      zoomLevel: 14
    },
    mirpur: {
      coordinates: [23.8223, 90.3654],
      zoomLevel: 14
    },
    gulshan: {
      coordinates: [23.7931, 90.4137],
      zoomLevel: 14
    },
    motijheel: {
      coordinates: [23.7331, 90.4173],
      zoomLevel: 14
    },
    lalbagh: {
      coordinates: [23.7179, 90.3872],
      zoomLevel: 14
    },
    dhanmondi: {
      coordinates: [23.7461, 90.3742],
      zoomLevel: 14
    },
    savar_sadar: {
      coordinates: [23.8583, 90.25],
      zoomLevel: 14
    },
    ashulia: {
      coordinates: [23.8917, 90.3167],
      zoomLevel: 14
    }
  },
  sheets: {
    uttara_sheet1: {
      coordinates: [23.8758, 90.3795],
      zoomLevel: 16
    },
    uttara_sheet2: {
      coordinates: [23.8748, 90.3815],
      zoomLevel: 16
    },
    mirpur_sheet1: {
      coordinates: [23.8223, 90.3654],
      zoomLevel: 16
    },
    mirpur_sheet2: {
      coordinates: [23.8243, 90.3674],
      zoomLevel: 16
    }
  }
};
// Helper component to update the map view when filters change
const MapUpdater = ({
  selectedFilters
}) => {
  const map = useMap();
  useEffect(() => {
    // Default view of Bangladesh
    let coordinates = [23.8103, 90.4125];
    let zoomLevel = 7;
    // Update coordinates and zoom level based on selected filters
    if (selectedFilters.sheetNo && locationData.sheets[selectedFilters.sheetNo]) {
      coordinates = locationData.sheets[selectedFilters.sheetNo].coordinates;
      zoomLevel = locationData.sheets[selectedFilters.sheetNo].zoomLevel;
    } else if (selectedFilters.mouja && locationData.moujas[selectedFilters.mouja]) {
      coordinates = locationData.moujas[selectedFilters.mouja].coordinates;
      zoomLevel = locationData.moujas[selectedFilters.mouja].zoomLevel;
    } else if (selectedFilters.upazila && locationData.upazilas[selectedFilters.upazila]) {
      coordinates = locationData.upazilas[selectedFilters.upazila].coordinates;
      zoomLevel = locationData.upazilas[selectedFilters.upazila].zoomLevel;
    } else if (selectedFilters.district && locationData.districts[selectedFilters.district]) {
      coordinates = locationData.districts[selectedFilters.district].coordinates;
      zoomLevel = locationData.districts[selectedFilters.district].zoomLevel;
    } else if (selectedFilters.division && locationData.divisions[selectedFilters.division]) {
      coordinates = locationData.divisions[selectedFilters.division].coordinates;
      zoomLevel = locationData.divisions[selectedFilters.division].zoomLevel;
    }
    // Set the map view
    map.setView(coordinates, zoomLevel);
  }, [selectedFilters, map]);
  return null;
};
// Helper component to expose map instance to controls
const MapController = ({
  onMapReady
}) => {
  const map = useMap();
  useEffect(() => {
    if (map) {
      onMapReady(map);
    }
  }, [map, onMapReady]);
  return null;
};
export const MapView = ({
  selectedFilters,
  addToCart
}: MapViewProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [geoData, setGeoData] = useState(null);
  const [selectedPlot, setSelectedPlot] = useState(null);
  const [popupPosition, setPopupPosition] = useState<[number, number]>([0, 0]);
  const [mapInstance, setMapInstance] = useState(null);
  const [mapMode, setMapMode] = useState('default'); // 'default', 'satellite', 'terrain'
  useEffect(() => {
    // This would fetch GeoJSON data based on the selected filters
    const fetchData = async () => {
      if (!selectedFilters.sheetNo) {
        setGeoData(null);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        // In a real app, this would be an API call
        // For this example, we'll simulate a delay and return mock data
        await new Promise(resolve => setTimeout(resolve, 1000));
        // Get coordinates for the selected sheet
        const sheetCoordinates = locationData.sheets[selectedFilters.sheetNo]?.coordinates || [23.8103, 90.4125];
        // Create mock GeoJSON data centered around the selected sheet with smaller plots
        const mockGeoData = {
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            properties: {
              id: '1',
              dagNo: '১২৩',
              khatians: [{
                id: 'k1',
                khatianNo: '১২৩৪৫',
                ownerName: 'আব্দুল করিম',
                area: '০.২৫ একর'
              }, {
                id: 'k2',
                khatianNo: '১২৩৪৬',
                ownerName: 'রহিম মিয়া',
                area: '০.১৫ একর'
              }]
            },
            geometry: {
              type: 'Polygon',
              coordinates: [[
              // Smaller polygon (reduced by 60%)
              [sheetCoordinates[1] - 0.002, sheetCoordinates[0] - 0.002], [sheetCoordinates[1] + 0.002, sheetCoordinates[0] - 0.002], [sheetCoordinates[1] + 0.002, sheetCoordinates[0] + 0.002], [sheetCoordinates[1] - 0.002, sheetCoordinates[0] + 0.002], [sheetCoordinates[1] - 0.002, sheetCoordinates[0] - 0.002]]]
            }
          }, {
            type: 'Feature',
            properties: {
              id: '2',
              dagNo: '১২৪',
              khatians: [{
                id: 'k3',
                khatianNo: '১২৩৪৭',
                ownerName: 'নূর জাহান',
                area: '০.৩০ একর'
              }]
            },
            geometry: {
              type: 'Polygon',
              coordinates: [[
              // Smaller polygon (reduced by 60%)
              [sheetCoordinates[1] + 0.003, sheetCoordinates[0] - 0.002], [sheetCoordinates[1] + 0.007, sheetCoordinates[0] - 0.002], [sheetCoordinates[1] + 0.007, sheetCoordinates[0] + 0.002], [sheetCoordinates[1] + 0.003, sheetCoordinates[0] + 0.002], [sheetCoordinates[1] + 0.003, sheetCoordinates[0] - 0.002]]]
            }
          },
          // Additional smaller plots for more variety
          {
            type: 'Feature',
            properties: {
              id: '3',
              dagNo: '১২৫',
              khatians: [{
                id: 'k4',
                khatianNo: '১২৩৪৮',
                ownerName: 'আলী আহমেদ',
                area: '০.২০ একর'
              }]
            },
            geometry: {
              type: 'Polygon',
              coordinates: [[[sheetCoordinates[1] - 0.003, sheetCoordinates[0] + 0.003], [sheetCoordinates[1] - 0.001, sheetCoordinates[0] + 0.003], [sheetCoordinates[1] - 0.001, sheetCoordinates[0] + 0.005], [sheetCoordinates[1] - 0.003, sheetCoordinates[0] + 0.005], [sheetCoordinates[1] - 0.003, sheetCoordinates[0] + 0.003]]]
            }
          }, {
            type: 'Feature',
            properties: {
              id: '4',
              dagNo: '১২৬',
              khatians: [{
                id: 'k5',
                khatianNo: '১২৩৪৯',
                ownerName: 'ফাতেমা বেগম',
                area: '০.১০ একর'
              }]
            },
            geometry: {
              type: 'Polygon',
              coordinates: [[[sheetCoordinates[1] + 0.001, sheetCoordinates[0] + 0.003], [sheetCoordinates[1] + 0.003, sheetCoordinates[0] + 0.003], [sheetCoordinates[1] + 0.003, sheetCoordinates[0] + 0.005], [sheetCoordinates[1] + 0.001, sheetCoordinates[0] + 0.005], [sheetCoordinates[1] + 0.001, sheetCoordinates[0] + 0.003]]]
            }
          }]
        };
        setGeoData(mockGeoData);
      } catch (err) {
        console.error('Error fetching map data:', err);
        setError('ডেটা লোড করতে সমস্যা হয়েছে। আবার চেষ্টা করুন।');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedFilters]);
  const handlePlotClick = (feature, event) => {
    console.log('Plot clicked:', feature.properties);
    setSelectedPlot(feature.properties);
    setPopupPosition([event.latlng.lat, event.latlng.lng]);
  };
  const closePopup = () => {
    setSelectedPlot(null);
  };
  // Define style for the GeoJSON features
  const plotStyle = {
    color: '#3388ff',
    weight: 2,
    opacity: 1,
    fillColor: '#3388ff',
    fillOpacity: 0.2
  };
  // Function to handle adding a khatian to cart with the correct mouja name
  const handleAddToCart = (khatian, dagNo) => {
    const khatianWithDetails = {
      ...khatian,
      dagNo: dagNo,
      moujaName: selectedFilters.mouja || 'মৌজা নাম' // Use selected mouja or default
    };
    addToCart(khatianWithDetails);
    // Optional: close the popup after adding to cart
    // closePopup();
  };
  const handleMapReady = map => {
    setMapInstance(map);
  };
  const handleZoomIn = () => {
    if (mapInstance) {
      mapInstance.zoomIn();
    }
  };
  const handleZoomOut = () => {
    if (mapInstance) {
      mapInstance.zoomOut();
    }
  };
  const handleMapModeChange = mode => {
    setMapMode(mode);
  };
  // Determine which tile layer to use based on map mode
  const getTileLayer = () => {
    switch (mapMode) {
      case 'satellite':
        return <TileLayer attribution='&copy; <a href="https://www.esri.com">Esri</a>' url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" data-id="element-213" />;
      case 'terrain':
        return <TileLayer attribution='&copy; <a href="https://www.opentopomap.org">OpenTopoMap</a>' url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png" data-id="element-214" />;
      default:
        return <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-215" />;
    }
  };
  return <div className="h-full relative" data-id="element-216">
      <MapContainer center={[23.8103, 90.4125]} zoom={7} style={{
      height: '100%',
      width: '100%'
    }} zoomControl={false} className="z-10" data-id="element-217">
        {getTileLayer()}
        <ZoomControl position="bottomright" data-id="element-218" />
        {geoData && <GeoJSON data={geoData} style={plotStyle} onEachFeature={(feature, layer) => {
        layer.on({
          click: e => {
            handlePlotClick(feature, e);
          }
        });
      }} data-id="element-219" />}
        {selectedPlot && <Popup position={popupPosition} onClose={closePopup} data-id="element-220">
            <PlotPopup plot={selectedPlot} onClose={closePopup} addToCart={khatian => handleAddToCart(khatian, selectedPlot.dagNo)} data-id="element-221" />
          </Popup>}
        <MapUpdater selectedFilters={selectedFilters} data-id="element-222" />
        <MapController onMapReady={handleMapReady} data-id="element-223" />
      </MapContainer>
      <MapControls onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} mapMode={mapMode} onMapModeChange={handleMapModeChange} data-id="element-224" />
      {loading && <LoadingState data-id="element-225" />}
      {error && <ErrorState message={error} data-id="element-226" />}
      {!loading && !error && geoData && geoData.features.length === 0 && <NoResultsState data-id="element-227" />}
    </div>;
};